import type {
  BookmarkResponse,
  ContentId,
  ContentIdList,
  ContentIxStatus,
  ContentIxStatus_ACTIONABLE,
  ContentIxStatus_EXPERT_VIDEO,
  ContentIxStatus_MIAB,
  ContentIxStatus_QUICKWIN,
  ContentIxStatus_SUMMARY,
  ContentIxStatus_VERIFAI,
  ContentIxStatusList,
} from "@newgenerated/shared/schema";
import type { Store } from "@/common/storeUtils";
import { DateTime } from "luxon";
import type { DeepReadonly } from "vue";
import { bookmarkMiab } from "@newgenerated/shared/stubs/bookmarkMiab";
import { getIxStatus } from "@newgenerated/shared/stubs/getIxStatus";
import { toggleBookmarkActionable } from "@newgenerated/shared/stubs/toggleBookmarkActionable";
import { toggleSummaryBookmark } from "@newgenerated/shared/stubs/toggleSummaryBookmark";
import { createSynchronizedQueue } from "@utils/requestUtils";
import type { ApiResult } from "@utils/httpClientCore";
import { assert } from "@utils/assertion";
import { bookmarkOnlineContent } from "@newgenerated/shared/stubs/bookmarkOnlineContent";

type IxWithStatus<T, K extends keyof T> = {
  [X in keyof Omit<T, K>]: { status: "DISABLED" | "READY"; value: Omit<T, K>[X] };
} & {
  [X in keyof Pick<T, K>]: Pick<T, K>[X];
};

export type ContentIxStatusDisplayActionable = IxWithStatus<ContentIxStatus_ACTIONABLE, "contentId" | "customerId" | "contentType">;
export type ContentIxStatusDisplaySummary = IxWithStatus<ContentIxStatus_SUMMARY, "contentId" | "customerId" | "contentType">;
export type ContentIxStatusDisplayMiab = IxWithStatus<ContentIxStatus_MIAB, "contentId" | "customerId" | "contentType" | "modifiedAt">;
export type ContentIxStatusDisplayExpertVideo = IxWithStatus<ContentIxStatus_EXPERT_VIDEO, "contentId" | "customerId" | "contentType" | "modifiedAt">;
export type ContentIxStatusDisplayQuickWin = IxWithStatus<ContentIxStatus_QUICKWIN, "contentId" | "customerId" | "contentType" | "modifiedAt">;
export type ContentIxStatusDisplayVerifai = IxWithStatus<ContentIxStatus_VERIFAI, "contentId" | "customerId" | "contentType" | "modifiedAt">;
export type ContentIxStatusDisplay = ContentIxStatusDisplayActionable | ContentIxStatusDisplaySummary | ContentIxStatusDisplayMiab | ContentIxStatusDisplayExpertVideo | ContentIxStatusDisplayQuickWin | ContentIxStatusDisplayVerifai;

type GetIxStatus = (id: ContentId) => ContentIxStatusDisplay;
type UpdateBookmark = (contentId: ContentId) => Promise<void>;
type UpdateItems = (ids: ContentId[], preserveExisting?: boolean) => void;
type RefreshIxStatus = (id: ContentId) => void;

export type BookmarkStatusHandler = {
  updateBookmark: UpdateBookmark;
  getIxStatus: GetIxStatus;
  refreshIxStatus: RefreshIxStatus;
};

export type IxStatusHandler = {
  updateItems: UpdateItems;
} & BookmarkStatusHandler;

export type IxStatusFetch = (props: { body: ContentIdList }) => Promise<ApiResult<ContentIxStatusList>>;

export type IxStatusHandlerApiMethods = {
  bookmarkMiab: (props: { miabId: bigint }) => Promise<BookmarkResponse>;
  bookmarkOnlineContent: (props: { onlineContentId: bigint; bookmarked: boolean }) => Promise<ApiResult<BookmarkResponse>>;
  toggleBookmarkActionable: ({ actionableId }: { actionableId: bigint }) => Promise<ApiResult<BookmarkResponse>>;
  toggleSummaryBookmark: ({ dataId }: { dataId: number }) => Promise<ApiResult<BookmarkResponse>>;
  getIxStatus: IxStatusFetch;
};
export type ContentIxStatusState = { ixStatuses: ContentIxStatusDisplay[] };

function getEmptyIxStatus(contentId: ContentId, status: "DISABLED" | "READY"): ContentIxStatusDisplay {
  switch (contentId.contentType) {
    case "SUMMARY":
      return {
        contentId: contentId,
        contentType: "SUMMARY",
        bookmarkedAt: { status: status, value: null },
        likedAt: { status: status, value: null },
        customerId: 0,
        consumedAt: { status: status, value: null },
        readingProgress: { status: status, value: 0 },
      };
    case "ACTIONABLE":
      return {
        contentId: contentId,
        bookmarkedAt: { status: status, value: null },
        experienceFeedbackScore: { status: status, value: null },
        peerInsightProvidedAt: { status: status, value: null },
        proTalkRequestedAt: { status: status, value: null },
        proTalkVideoProgress: { status: status, value: null },
        step2CompletedAt: { status: status, value: null },
        step2Readiness: { status: status, value: false },
        step2VideoProgress: { status: status, value: null },
        customerId: 0,
        contentType: "ACTIONABLE",
      };

    case "MIAB":
      return {
        contentId: contentId,
        bookmarkedAt: { status: status, value: null },
        feedbackAt: { status: status, value: null },
        modifiedAt: DateTime.now(),
        feedback: { status: status, value: null },
        customerId: 0,
        contentType: "MIAB",
      };
    case "QUICKWIN":
      return {
        contentId: contentId,
        contentType: "QUICKWIN",
        customerId: 0,
        selectedAnswerIndex: { status: status, value: null },
        startedAt: { status: status, value: null },
        completedAt: { status: status, value: null },
        reshuffledAt: { status: status, value: null },
        bookmarkedAt: { status: status, value: null },
        modifiedAt: DateTime.now(),
      };
    case "EXPERT_VIDEO":
      return {
        contentId: contentId,
        contentType: "EXPERT_VIDEO",
        customerId: 0,
        startedAt: { status: status, value: null },
        bookmarkedAt: { status: status, value: null },
        modifiedAt: DateTime.now(),
      };
    case "ONLINE_CONTENT":
      return {
        contentId: contentId,
        contentType: "VERIFAI",
        customerId: 0,
        bookmarkedAt: { status: status, value: null },
        consumedAt: { status: status, value: null },
        modifiedAt: DateTime.now(),
      };
  }
}

function getStatus(contentId: DeepReadonly<ContentId>, ixStatuses: DeepReadonly<ContentIxStatusDisplay[]>): ContentIxStatusDisplay {
  const status = ixStatuses.find((ix) => ix.contentId.id === contentId.id && ix.contentId.contentType === contentId.contentType);
  if (status !== undefined) {
    return status;
  }
  return getEmptyIxStatus(contentId, "DISABLED");
}

export function convertIxStatusToDisplay(ix: ContentIxStatus): ContentIxStatusDisplay {
  switch (ix.contentType) {
    case "ACTIONABLE":
      return convertActionableIxToDisplay(ix);
    case "SUMMARY":
      return convertSummaryIxToDisplay(ix);
    case "MIAB":
      return convertMiabIxToDisplay(ix);
    case "EXPERT_VIDEO":
      return convertExpertVideoIxToDisplay(ix);
    case "QUICKWIN":
      return convertQuickWinIxToDisplay(ix);
    case "VERIFAI":
      return convertVerifaiIxToDisplay(ix);
  }
}

function convertExpertVideoIxToDisplay(ix: ContentIxStatus_EXPERT_VIDEO): ContentIxStatusDisplayExpertVideo {
  return {
    contentId: ix.contentId,
    contentType: ix.contentType,
    customerId: 0,
    modifiedAt: ix.modifiedAt,
    startedAt: { status: "READY", value: ix.startedAt },
    bookmarkedAt: { status: "READY", value: null },
  };
}

function convertSummaryIxToDisplay(ix: ContentIxStatus_SUMMARY): ContentIxStatusDisplaySummary {
  return {
    bookmarkedAt: { status: "READY", value: ix.bookmarkedAt },
    consumedAt: { status: "READY", value: ix.consumedAt },
    contentId: ix.contentId,
    contentType: ix.contentType,
    customerId: 0,
    likedAt: { status: "READY", value: ix.likedAt },
    readingProgress: { status: "READY", value: ix.readingProgress },
  };
}

function convertVerifaiIxToDisplay(ix: ContentIxStatus_VERIFAI): ContentIxStatusDisplayVerifai {
  return {
    contentId: ix.contentId,
    contentType: ix.contentType,
    customerId: ix.customerId,
    modifiedAt: ix.modifiedAt,
    bookmarkedAt: { status: "READY", value: ix.bookmarkedAt },
    consumedAt: { status: "READY", value: ix.consumedAt },
  };
}

function convertMiabIxToDisplay(ix: ContentIxStatus_MIAB): ContentIxStatusDisplayMiab {
  return {
    bookmarkedAt: { status: "READY", value: ix.bookmarkedAt },
    contentId: ix.contentId,
    contentType: ix.contentType,
    customerId: 0,
    feedback: { status: "READY", value: ix.feedback },
    feedbackAt: { status: "READY", value: ix.feedbackAt },
    modifiedAt: ix.modifiedAt,
  };
}

function convertQuickWinIxToDisplay(ix: ContentIxStatus_QUICKWIN): ContentIxStatusDisplayQuickWin {
  return {
    contentId: ix.contentId,
    contentType: ix.contentType,
    customerId: 0,
    selectedAnswerIndex: { status: "READY", value: ix.selectedAnswerIndex },
    startedAt: { status: "READY", value: ix.startedAt },
    completedAt: { status: "READY", value: ix.completedAt },
    reshuffledAt: { status: "READY", value: ix.reshuffledAt },
    bookmarkedAt: { status: "READY", value: null },
    modifiedAt: ix.modifiedAt,
  };
}

function convertActionableIxToDisplay(ix: ContentIxStatus_ACTIONABLE): ContentIxStatusDisplayActionable {
  return {
    bookmarkedAt: { status: "READY", value: ix.bookmarkedAt },
    experienceFeedbackScore: { status: "READY", value: ix.experienceFeedbackScore },
    peerInsightProvidedAt: { status: "READY", value: ix.peerInsightProvidedAt },
    proTalkRequestedAt: { status: "READY", value: ix.proTalkRequestedAt },
    proTalkVideoProgress: { status: "READY", value: ix.proTalkVideoProgress },
    step2CompletedAt: { status: "READY", value: ix.step2CompletedAt },
    step2Readiness: { status: "READY", value: ix.step2Readiness },
    step2VideoProgress: { status: "READY", value: ix.step2VideoProgress },
    contentId: ix.contentId,
    contentType: ix.contentType,
    customerId: 0,
  };
}

export function getInitialContentIxStatusState(): ContentIxStatusState {
  return {
    ixStatuses: [],
  };
}

/**
 * Fetches Content Ix status of `ids` and updates the store accordingly.
 * @param preserveExisting - If true, merges fetched statuses with existing ones; if false, replaces entire store
 * In case of an error, preserveExisting=true keeps existing statuses unchanged, preserveExisting=false clears the store.
 */
async function updateContentIxStatuses(store: Store<ContentIxStatusState>, ids: ContentId[], fetch: IxStatusFetch, preserveExisting: boolean): Promise<void> {
  const response = await fetch({ body: { items: ids } });
  if (response.kind === "error") {
    if (!preserveExisting) {
      store.sub("ixStatuses").set([]);
    }
    // If preserveExisting=true, do nothing on error (keep existing statuses)
  } else {
    const fetchedContentIxStatuses = response.data.items.map((ix) => convertIxStatusToDisplay(ix));

    if (preserveExisting) {
      // Merge new statuses with existing ones
      const statusMap = new Map(store.get().ixStatuses.map((s) => [`${s.contentId.id}-${s.contentId.contentType}`, s]));
      fetchedContentIxStatuses.forEach((s) => statusMap.set(`${s.contentId.id}-${s.contentId.contentType}`, s));
      store.sub("ixStatuses").set(Array.from(statusMap.values()));
    } else {
      // Replace the entire store with new statuses
      store.sub("ixStatuses").set(fetchedContentIxStatuses);
    }
  }
}

export function createIxStatusHandler(store: Store<ContentIxStatusState>, apiMethods?: IxStatusHandlerApiMethods): IxStatusHandler {
  const methods = apiMethods ?? {
    bookmarkMiab: bookmarkMiab,
    toggleBookmarkActionable: toggleBookmarkActionable,
    toggleSummaryBookmark: toggleSummaryBookmark,
    getIxStatus: getIxStatus,
    bookmarkOnlineContent: bookmarkOnlineContent,
  };
  const queue = createSynchronizedQueue();

  const onUpdate = async (contentId: ContentId): Promise<void> => {
    const index = store.get().ixStatuses.findIndex((ix) => ix.contentId.id === contentId.id && ix.contentId.contentType === contentId.contentType);
    if (index > -1) {
      const existing = store.sub("ixStatuses").sub(index);
      existing.sub("bookmarkedAt").sub("status").set("DISABLED");

      switch (contentId.contentType) {
        case "MIAB":
          try {
            const result = await methods.bookmarkMiab({ miabId: BigInt(contentId.id) });
            if ("kind" in result) {
              assert(result.kind === "success");
              existing.sub("bookmarkedAt").set({ status: "READY", value: result.data.bookmarked ? DateTime.now() : null });
            } else {
              existing.sub("bookmarkedAt").set({ status: "READY", value: result.bookmarked ? DateTime.now() : null });
            }
          } catch (_) {
            existing.sub("bookmarkedAt").sub("status").set("READY");
          }
          return;
        case "ACTIONABLE": {
          const result = await methods.toggleBookmarkActionable({ actionableId: contentId.id });
          if (result.kind === "success") {
            existing.sub("bookmarkedAt").set({ status: "READY", value: result.data.bookmarked ? DateTime.now() : null });
          } else {
            existing.sub("bookmarkedAt").sub("status").set("READY");
          }
          return;
        }
        case "SUMMARY": {
          try {
            const result = await methods.toggleSummaryBookmark({ dataId: Number(contentId.id) });
            if (result.kind === "success") {
              existing.sub("bookmarkedAt").set({ status: "READY", value: result.data.bookmarked ? DateTime.now() : null });
            }
          } catch (_) {
            existing.sub("bookmarkedAt").sub("status").set("READY");
          }
          return;
        }
        case "ONLINE_CONTENT": {
          try {
            const result = await methods.bookmarkOnlineContent({ onlineContentId: contentId.id, bookmarked: existing.get().bookmarkedAt.value === null });
            if (result.kind === "success") {
              existing.sub("bookmarkedAt").set({ status: "READY", value: result.data.bookmarked ? DateTime.now() : null });
            }
          } catch (_) {
            existing.sub("bookmarkedAt").sub("status").set("READY");
          }
          return;
        }
        case "EXPERT_VIDEO": {
          throw new Error('Not implemented yet: "EXPERT_VIDEO" case');
        }
        case "QUICKWIN": {
          // QuickWin doesn't support bookmarking, only completion tracking
          return;
        }
      }
    }
  };

  const updateItems = (contentIds: ContentId[], preserveExisting = false): void => {
    queue.run(async (): Promise<void> => {
      try {
        await updateContentIxStatuses(store, contentIds, methods.getIxStatus, preserveExisting);
      } catch (_) {
        // do nothing, nothing is updated
      }
    });
  };

  const refreshIxStatus = (id: ContentId): void => {
    updateItems([id], true);
  };

  return {
    getIxStatus: (id: ContentId): ContentIxStatusDisplay => getStatus(id, store.sub("ixStatuses").get()),
    updateItems: updateItems,
    updateBookmark: onUpdate,
    refreshIxStatus: refreshIxStatus,
  };
}

export type ContentProgressState = "NONE" | "STARTED" | "COMPLETED";

export function getContentProgressState(ixStatus: ContentIxStatusDisplay | undefined): ContentProgressState {
  if (ixStatus === undefined) {
    return "NONE";
  }
  switch (ixStatus.contentType) {
    case "QUICKWIN":
      if (ixStatus.completedAt.value !== null) return "COMPLETED";
      if (ixStatus.startedAt.value !== null) return "STARTED";
      return "NONE";

    case "SUMMARY":
      if (ixStatus.consumedAt.value !== null) return "COMPLETED";
      return "NONE";

    case "MIAB":
      return "NONE";

    case "EXPERT_VIDEO":
      return "NONE";

    case "ACTIONABLE":
      return "NONE";
    case "VERIFAI": {
      if (ixStatus.consumedAt.value !== null) return "COMPLETED";
      return "NONE";
    }
  }
}
